import { useState, useRef, useEffect } from "react";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

/**
 * Reusable image upload component with preview
 * @param {string} uploadType - 'forum' or 'chat'
 * @param {function} onImageUploaded - Callback when image is uploaded (receives URL)
 * @param {function} onImageRemoved - Callback when image is removed
 * @param {string} currentImage - Current image URL (for preview)
 * @param {boolean} compact - Use compact mode for chat
 * @param {boolean} multiple - Allow multiple images (for forum)
 * @param {string[]} currentImages - Current images array (for multiple mode)
 * @param {function} onImagesUpdated - Callback for multiple images
 */
const ImageUpload = ({ 
  uploadType = "forum", 
  onImageUploaded, 
  onImageRemoved,
  currentImage,
  compact = false,
  multiple = false,
  currentImages = [],
  onImagesUpdated
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [previews, setPreviews] = useState(currentImages || []);
  const fileInputRef = useRef(null);

  // Sync internal preview state with parent's currentImage prop
  useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  // Sync internal previews state with parent's currentImages prop
  useEffect(() => {
    setPreviews(currentImages || []);
  }, [currentImages]);

  const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      if (multiple) {
        // Handle multiple files
        const uploadPromises = Array.from(files).map(file => uploadFile(file));
        const urls = await Promise.all(uploadPromises);
        const newPreviews = [...previews, ...urls];
        setPreviews(newPreviews);
        if (onImagesUpdated) onImagesUpdated(newPreviews);
      } else {
        // Single file upload
        const url = await uploadFile(files[0]);
        setPreview(url);
        if (onImageUploaded) onImageUploaded(url);
      }
    } catch (err) {
      console.error("Upload failed:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to upload image";
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const uploadFile = async (file) => {
    // Validate file type - allow common image types including HEIC
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    // Also check by extension for HEIC since browser may not report correct MIME type
    const ext = file.name.toLowerCase().split('.').pop();
    const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    
    if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
      throw new Error("Invalid file type. Allowed: JPG, PNG, GIF, WebP, HEIC");
    }

    // Validate file size (50MB max - server will resize and compress)
    if (file.size > 50 * 1024 * 1024) {
      throw new Error("File too large. Maximum size: 50MB");
    }

    const formData = new FormData();
    formData.append("file", file);

    const endpoint = uploadType === "chat" ? "/upload/chat-image" : "/upload/forum-image";
    const response = await axios.post(`${API}${endpoint}`, formData, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "multipart/form-data"
      }
    });

    return response.data.image_url;
  };

  const handleRemove = (index = null) => {
    if (multiple && index !== null) {
      const newPreviews = previews.filter((_, i) => i !== index);
      setPreviews(newPreviews);
      if (onImagesUpdated) onImagesUpdated(newPreviews);
    } else {
      setPreview(null);
      if (onImageRemoved) onImageRemoved();
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Compact mode for chat - use currentImage prop directly for controlled behavior
  if (compact) {
    return (
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {currentImage ? (
          <div className="relative inline-block">
            <img 
              src={currentImage.startsWith('/api') ? `${BACKEND_URL}${currentImage}` : currentImage}
              alt="Preview" 
              className="w-16 h-16 object-cover rounded-lg border"
            />
            <button
              type="button"
              onClick={() => { if (onImageRemoved) onImageRemoved(); }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={triggerFileInput}
            disabled={uploading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Attach image"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
            ) : (
              <ImagePlus className="w-5 h-5 text-gray-500" />
            )}
          </button>
        )}
      </div>
    );
  }

  // Full mode for forum
  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Preview area */}
      {multiple ? (
        <div className="flex flex-wrap gap-2">
          {previews.map((url, index) => (
            <div key={index} className="relative group">
              <img 
                src={url.startsWith('/api') ? `${BACKEND_URL}${url}` : url}
                alt={`Preview ${index + 1}`}
                className="w-24 h-24 object-cover rounded-lg border"
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          {/* Add more button */}
          <button
            type="button"
            onClick={triggerFileInput}
            disabled={uploading}
            className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-cyan-500 hover:bg-cyan-50 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            ) : (
              <>
                <ImagePlus className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Add</span>
              </>
            )}
          </button>
        </div>
      ) : preview ? (
        <div className="relative inline-block">
          <img 
            src={preview.startsWith('/api') ? `${BACKEND_URL}${preview}` : preview}
            alt="Preview" 
            className="max-w-xs max-h-48 object-contain rounded-lg border"
          />
          <button
            type="button"
            onClick={() => handleRemove()}
            className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={triggerFileInput}
          disabled={uploading}
          className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-cyan-500 hover:bg-cyan-50 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
              <span className="text-sm text-gray-500 mt-2">Uploading...</span>
            </>
          ) : (
            <>
              <ImagePlus className="w-8 h-8 text-gray-400" />
              <span className="text-sm text-gray-500 mt-2">Click to upload image</span>
              <span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP, HEIC (auto-compressed)</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
